import { useEffect, useState } from "react";
import { IdCard, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { EliminarPerfilDialog } from "@/components/perfiles/EliminarPerfilDialog";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePerfiles } from "@/hooks/usePerfiles";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import type { Perfil } from "@/lib/perfiles";

const FILAS_SKELETON = 3;

/**
 * Pantalla 10 SA — Mantenedor de perfiles.
 *
 * Los perfiles semilla (Super Admin / Usuario Normal / Usuario Revisor) no se
 * pueden eliminar: el sistema tiene lógica asociada a ellos. En vez de ofrecer
 * la acción y hacer chocar al usuario contra un 422, la fila muestra el motivo
 * en el lugar donde iría el botón.
 */
export function Perfiles() {
  const perfiles = usePerfiles();
  const [aEliminar, setAEliminar] = useState<Perfil | null>(null);

  /* El servidor es la autoridad sobre qué se puede borrar. Si rechaza un
     borrado sin darle salida, la pantalla se corrige y deja de ofrecer la
     acción para esa fila.

     Se LIMPIA en cuanto llegan datos frescos del servidor (`dataUpdatedAt`):
     la corrección es una conjetura local sobre una respuesta que ya no es la
     última, y dejarla acumulada retiraba el borrado para siempre —incluso si
     el motivo del rechazo fue pasajero. La verdad estable viaja en el payload
     (`es_semilla`), no en esta lista. */
  const [bloqueadosSegunServidor, setBloqueadosSegunServidor] = useState<number[]>([]);
  const datosFrescosEn = perfiles.dataUpdatedAt;
  useEffect(() => {
    setBloqueadosSegunServidor([]);
  }, [datosFrescosEn]);

  const total = perfiles.data?.length ?? 0;
  // Los cuatro estados de la consulta, incluidos los dos que la terna
  // isError/isLoading/data no distingue (ver `lib/estadoDeConsulta.ts`).
  const estado = estadoDeConsulta(perfiles);

  return (
    <div className="space-y-6 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-muted-foreground text-caption">
            {perfiles.data
              ? `${total} perfiles configurados`
              : "Perfiles de acceso que se asignan a cada usuario"}
          </p>
          <p className="text-ink-hint mt-0.5 text-xs">
            Los perfiles del sistema no se eliminan: la plataforma tiene lógica asociada a
            ellos. Renombrarlos sí está permitido.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/perfiles/nuevo">
            <Plus className="h-4 w-4" />
            Crear perfil
          </Link>
        </Button>
      </div>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar los perfiles"
          error={perfiles.error}
          onReintentar={() => perfiles.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que="los perfiles" />
      ) : !estado.cargando && total === 0 ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <IdCard />
            </EmptyMedia>
            <EmptyTitle>Todavía no hay perfiles</EmptyTitle>
            <EmptyDescription>
              Crea el primero para poder asignárselo a los usuarios.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button asChild>
              <Link to="/admin/perfiles/nuevo">
                <Plus className="h-4 w-4" />
                Crear perfil
              </Link>
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <div className="bg-card overflow-hidden rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="px-6">Perfil</TableHead>
                <TableHead>Usuarios asignados</TableHead>
                <TableHead className="px-6 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {estado.cargando
                ? Array.from({ length: FILAS_SKELETON }).map((_, i) => (
                    // El `aria-label` no es decoración: es lo que deja que un
                    // test afirme que la carga se dibuja (y que borrar esta
                    // rama ponga la suite en rojo).
                    <TableRow key={i} aria-label="Cargando perfil" className="hover:bg-transparent">
                      <TableCell className="px-6">
                        <Skeleton className="h-3.5 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-3.5 w-8" />
                      </TableCell>
                      <TableCell className="px-6">
                        <Skeleton className="ml-auto h-8 w-40 rounded-md" />
                      </TableCell>
                    </TableRow>
                  ))
                : perfiles.data?.map((perfil) => {
                    const bloqueado = bloqueadosSegunServidor.includes(perfil.id);

                    return (
                      <TableRow key={perfil.id}>
                        <TableCell className="px-6">
                          <span className="text-[0.8125rem] font-bold">{perfil.nombre}</span>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono font-semibold">
                          {perfil.usuarios_count ?? "—"}
                        </TableCell>
                        <TableCell className="px-6">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button asChild variant="ghost" size="sm">
                              <Link to={`/admin/perfiles/${perfil.id}/editar`}>
                                <Pencil className="h-3.5 w-3.5" />
                                Editar
                              </Link>
                            </Button>
                            {perfil.es_semilla || bloqueado ? (
                              <span className="text-ink-hint inline-flex items-center gap-1.5 px-3.5 text-xs">
                                <Lock className="h-3.5 w-3.5" />
                                {perfil.es_semilla ? "Perfil del sistema" : "Bloqueado por el sistema"}
                              </span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setAEliminar(perfil)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* El `key` REMONTA el diálogo al cambiar de perfil: sin él, el rechazo
          que dejó el borrado anterior (la alerta roja, y el botón retirado)
          seguiría en pantalla sobre un perfil que nunca se intentó borrar. */}
      <EliminarPerfilDialog
        key={aEliminar?.id}
        perfil={aEliminar}
        onCerrar={() => setAEliminar(null)}
        onBloqueado={(id) => setBloqueadosSegunServidor((ids) => [...ids, id])}
      />
    </div>
  );
}
